import React, { useState, useEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { fetchActiveCities } from "../../../redux/slice/locationSlice";
import { fetchCompanyCategories } from "../../../redux/slice/companySlice";
import InputField from "../../../components/form/InputField";
import CustomSelect from "../../../components/form/CustomSelect";
import { CheckboxOption, MultiValueMessage, multiMessageStyle } from "../../../components/form/utils";

const HybridRangeFilter = ({ label, prefix, filters, handleChange }) => {
  const [isCustomMode, setIsCustomMode] = useState(false);

  const minKey = `min_${prefix}`;
  const maxKey = `max_${prefix}`;
  const minVal = filters[minKey] || "";
  const maxVal = filters[maxKey] || "";

  let dropdownValue = "any";
  if (minVal === "0" && maxVal === "10") dropdownValue = "0-10";
  else if (minVal === "11" && maxVal === "50") dropdownValue = "11-50";
  else if (minVal === "51" && maxVal === "100") dropdownValue = "51-100";
  else if (minVal === "101" && maxVal === "500") dropdownValue = "101-500";
  else if (minVal === "501" && maxVal === "") dropdownValue = "500+";
  else if (minVal !== "" || maxVal !== "") dropdownValue = "custom";
  else if (isCustomMode) dropdownValue = "custom";

  const options = [
    { value: "any", label: "Any" },
    { value: "0-10", label: "0 to 10" },
    { value: "11-50", label: "11 to 50" },
    { value: "51-100", label: "51 to 100" },
    { value: "101-500", label: "101 to 500" },
    { value: "500+", label: "500+" },
    { value: "custom", label: "Custom Range..." },
  ];

  const handleDropdownChange = (selected) => {
    const val = selected ? selected.value : "any";
    if (val === "custom") {
      setIsCustomMode(true);
      handleChange(minKey, "");
      handleChange(maxKey, "");
    } else {
      setIsCustomMode(false);
      if (val === "any") {
        handleChange(minKey, "");
        handleChange(maxKey, "");
      } else if (val === "0-10") {
        handleChange(minKey, "0");
        handleChange(maxKey, "10");
      } else if (val === "11-50") {
        handleChange(minKey, "11");
        handleChange(maxKey, "50");
      } else if (val === "51-100") {
        handleChange(minKey, "51");
        handleChange(maxKey, "100");
      } else if (val === "101-500") {
        handleChange(minKey, "101");
        handleChange(maxKey, "500");
      } else if (val === "500+") {
        handleChange(minKey, "501");
        handleChange(maxKey, "");
      }
    }
  };

  return (
    <div>

      <CustomSelect
        options={options}
        value={options.find(o => o.value === dropdownValue) || options[0]}
        onChange={handleDropdownChange}
        className="w-100 mb-2"
        isClearable={false}
        label={{ name: label }}
      />
      {dropdownValue === "custom" && (
        <div className="grid gap-2 animate-fade-in" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          <div>
            <Form.Control
              type="number"
              placeholder="Min"
              value={minVal}
              onChange={(e) => handleChange(minKey, e.target.value)}
            />
          </div>
          <div>
            <Form.Control
              type="number"
              placeholder="Max"
              value={maxVal}
              onChange={(e) => handleChange(maxKey, e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const CompanyFilterDrawer = ({ initialFilters, onChange }) => {
  const dispatch = useDispatch();

  const { cities = [] } = useSelector((state) => state.location || {});
  const { companyCategories } = useSelector((state) => state.company || {});

  const cityOptions = cities.map(c => ({ value: c.id, label: c.name }));
  const categoryOptions = (companyCategories?.data || []).map(c => ({ value: c.id, label: c.name }));

  const citySearchTimeoutRef = useRef(null);
  const categorySearchTimeoutRef = useRef(null);

  const handleCitySearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
      citySearchTimeoutRef.current = setTimeout(() => {
        dispatch(fetchActiveCities({ search: inputValue, limit: 20 }));
      }, 400);
    } else if (action === "menu-close") {
      dispatch(fetchActiveCities({ limit: 20 }));
    }
  };

  const handleCategorySearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (categorySearchTimeoutRef.current) clearTimeout(categorySearchTimeoutRef.current);
      categorySearchTimeoutRef.current = setTimeout(() => {
        dispatch(fetchCompanyCategories({ search: inputValue, limit: 20 }));
      }, 400);
    } else if (action === "menu-close") {
      dispatch(fetchCompanyCategories({ limit: 20 }));
    }
  };

  const [selectedCityObjects, setSelectedCityObjects] = useState(() =>
    (initialFilters?.city_id || []).map(id => cityOptions.find(c => c.value === id) || { value: id, label: "Selected City" })
  );

  const [selectedCategoryObjects, setSelectedCategoryObjects] = useState(() =>
    (initialFilters?.category_id || []).map(id => categoryOptions.find(c => c.value === id) || { value: id, label: "Selected Category" })
  );

  const mergedCityOptions = [
    ...selectedCityObjects,
    ...cityOptions.filter(c => !selectedCityObjects.some(sc => sc.value === c.value))
  ];

  const mergedCategoryOptions = [
    ...selectedCategoryObjects,
    ...categoryOptions.filter(c => !selectedCategoryObjects.some(sc => sc.value === c.value))
  ];

  const [filters, setFilters] = useState(initialFilters || {
    city_id: [],
    category_id: [],
    verification_status: null,
    status: null,
    min_followers: "", max_followers: "",
    min_posts: "", max_posts: "",
    start_date: "", end_date: ""
  });

  useEffect(() => {
    if (onChange) {
      onChange(filters);
    }
  }, [filters, onChange]);

  const handleChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="d-flex flex-column gap-3">


      <CustomSelect
        options={mergedCityOptions}
        value={selectedCityObjects}
        isMulti
        isClearable
        closeMenuOnSelect={false}
        selectProps={{
          components: { Option: CheckboxOption, MultiValue: (props) => <MultiValueMessage {...props} message="Cities Selected" /> },
          styles: multiMessageStyle
        }}
        placeholder="Select City"
        onInputChange={handleCitySearch}
        onChange={(selected) => {
          const selectedArr = selected || [];
          setSelectedCityObjects(selectedArr);
          handleChange("city_id", selectedArr.map(s => s.value));
        }}
        label={{ name: "City" }}
      />



      <CustomSelect
        options={mergedCategoryOptions}
        value={selectedCategoryObjects}
        isMulti
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        selectProps={{
          components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
          styles: multiMessageStyle
        }}
        placeholder="Select Category"
        onInputChange={handleCategorySearch}
        onChange={(selected) => {
          const selectedArr = selected || [];
          setSelectedCategoryObjects(selectedArr);
          handleChange("category_id", selectedArr.map(s => s.value));
        }}
        label={{ name: "Category" }}
      />



      <CustomSelect
        options={[
          { value: "pending", label: "Pending" },
          { value: "in_progress", label: "In Progress" },
          { value: "submitted", label: "Submitted" },
          { value: "verified", label: "Verified" },
          { value: "rejected", label: "Rejected" }
        ]}
        value={
          filters.verification_status
            ? [
              { value: "pending", label: "Pending" },
              { value: "in_progress", label: "In Progress" },
              { value: "submitted", label: "Submitted" },
              { value: "verified", label: "Verified" },
              { value: "rejected", label: "Rejected" }
            ].find(o => o.value === filters.verification_status) || null
            : null
        }
        isClearable
        placeholder="Any"
        onChange={(selected) => handleChange("verification_status", selected ? selected.value : null)}
        label={{ name: "Verification Status" }}
      />


      <CustomSelect
        options={[
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" }
        ]}
        value={
          filters.status
            ? [
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" }
            ].find(o => o.value === filters.status) || null
            : null
        }
        isClearable
        placeholder="Any"
        onChange={(selected) => handleChange("status", selected ? selected.value : null)}
        label={{ name: "Status" }}
      />


      <HybridRangeFilter
        label="Followers"
        prefix="followers"
        filters={filters}
        handleChange={handleChange}
      />

      <HybridRangeFilter
        label="Posts"
        prefix="posts"
        filters={filters}
        handleChange={handleChange}
      />

      <InputField
        label={{ name: "Created At", class: "text-muted fw-semibold mb-2" }}
        input={{
          type: "date-range",
          value: { start: filters.start_date, end: filters.end_date },
          placeholder: "Select date range",
          onChange: (e) => {
            const val = e.target.value;
            handleChange("start_date", val?.start || "");
            handleChange("end_date", val?.end || "");
          }
        }}
      />
    </div>
  );
};

export default CompanyFilterDrawer;
